import { useCallback, useEffect, useRef, useState } from "react";

import { scheduleWaitingNotice } from "@/lib/waitingNotice.mjs";

const TH = 0.035;
const BARGE_TH = 0.09;

export function useVoice({ serverVoice, onUtterance, onInterrupt, onStart, onWaiting, endOfSpeechMs = 900, lang = "fr-FR" }) {
  const [state, setState] = useState("idle");
  const [level, setLevel] = useState(0);
  const [error, setError] = useState(null);
  const r = useRef({});
  const stateRef = useRef("idle");
  const epoch = useRef(0);
  const setS = (s) => { stateRef.current = s; setState(s); };
  const cbs = useRef({});
  cbs.current = { onUtterance, onInterrupt, onStart, onWaiting };

  const finish = useCallback(async (blob, text, opening = false) => {
    const ticket = epoch.current;
    setS("processing");
    try {
      const cancelWaiting = !opening && cbs.current.onWaiting ? scheduleWaitingNotice({
        isCurrent: () => ticket === epoch.current && stateRef.current === "processing",
        load: () => cbs.current.onWaiting(),
        dispose: (reply) => { if (reply?.audioUrl) URL.revokeObjectURL(reply.audioUrl); },
        play: (reply) => {
          if (reply?.audioUrl) {
            const audio = new Audio(reply.audioUrl);
            const cleanup = () => { audio.pause(); URL.revokeObjectURL(reply.audioUrl); };
            audio.play().catch(() => {});
            return cleanup;
          }
          if (reply?.speakText && window.speechSynthesis) {
            const utterance = new SpeechSynthesisUtterance(reply.speakText);
            utterance.lang = lang;
            window.speechSynthesis.speak(utterance);
            return () => window.speechSynthesis.cancel();
          }
        },
      }) : () => {};
      r.current.cancelWaiting = cancelWaiting;
      let reply;
      try {
        reply = opening ? await cbs.current.onStart?.() : await cbs.current.onUtterance(blob, text);
      } finally {
        cancelWaiting();
        if (r.current.cancelWaiting === cancelWaiting) delete r.current.cancelWaiting;
      }
      if (ticket !== epoch.current || stateRef.current !== "processing") {
        if (reply?.audioUrl) URL.revokeObjectURL(reply.audioUrl);
        return;
      }
      if (reply?.audioUrl) {
        if (r.current.audioUrl) URL.revokeObjectURL(r.current.audioUrl);
        const audio = new Audio(reply.audioUrl);
        r.current.audio = audio;
        r.current.audioUrl = reply.audioUrl;
        setS("speaking");
        await new Promise((resolve, reject) => {
          r.current.resolvePlayback = resolve;
          audio.onended = () => { if (stateRef.current === "speaking") setS("listening"); resolve(); };
          audio.onerror = () => reject(new Error("Lecture audio impossible"));
          audio.play().catch(reject);
        });
      } else if (reply?.speakText && window.speechSynthesis) {
        const u = new SpeechSynthesisUtterance(reply.speakText);
        u.lang = lang;
        const voices = window.speechSynthesis.getVoices();
        const preferredVoice =
          voices.find((v) => v.name === "Google français") ||
          voices.find((v) => v.lang === lang);
        if (preferredVoice) u.voice = preferredVoice;
        u.rate = 1;
        setS("speaking");
        await new Promise((resolve, reject) => {
          r.current.resolvePlayback = resolve;
          u.onend = () => { if (stateRef.current === "speaking") setS("listening"); resolve(); };
          u.onerror = () => reject(new Error("Synthèse vocale interrompue ou indisponible"));
          window.speechSynthesis.speak(u);
        });
      } else setS("listening");
    } catch (e) {
      if (ticket !== epoch.current) return;
      setError(e.message || String(e));
      if (stateRef.current !== "idle") setS("listening");
    }
  }, [lang]);

  const interrupt = useCallback(() => {
    epoch.current += 1;
    r.current.cancelWaiting?.();
    r.current.resolvePlayback?.();
    r.current.audio?.pause();
    window.speechSynthesis?.cancel();
    setS("listening");
    cbs.current.onInterrupt?.();
  }, []);

  const startRec = () => {
    const ticket = epoch.current;
    const rec = new MediaRecorder(r.current.stream);
    const chunks = [];
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    rec.onstop = () => {
      const dur = Date.now() - rec._t0;
      if (ticket !== epoch.current || dur < 500 || stateRef.current !== "listening") return;
      finish(new Blob(chunks, { type: rec.mimeType || "audio/webm" }));
    };
    rec._t0 = Date.now();
    rec.start();
    r.current.recorder = rec;
  };

  const loop = () => {
    const { analyser, buf } = r.current;
    if (!analyser) return;
    analyser.getFloatTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    const rms = Math.sqrt(sum / buf.length);
    setLevel(rms);
    const now = Date.now();
    if (stateRef.current === "listening") {
      if (rms > TH) {
        r.current.lastVoice = now;
        if (!r.current.recorder) startRec();
      } else if (r.current.recorder && now - r.current.lastVoice > endOfSpeechMs) {
        const rec = r.current.recorder;
        r.current.recorder = null;
        rec.stop();
      }
    } else if (stateRef.current === "speaking") {
      r.current.barge = rms > BARGE_TH ? (r.current.barge || 0) + 1 : 0;
      if (r.current.barge > 6) {
        r.current.barge = 0;
        interrupt();
        r.current.lastVoice = now;
        startRec();
      }
    }
    r.current.raf = requestAnimationFrame(loop);
  };

  const startBrowser = () => {
    const browserSession = r.current;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) throw new Error("Reconnaissance vocale non disponible dans ce navigateur (essayez Chrome)");
    const rec = new SR();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = (e) => {
      if (browserSession !== r.current) return;
      const res = e.results[e.results.length - 1];
      if (stateRef.current === "speaking") interrupt();
      if (res.isFinal && stateRef.current === "listening") finish(null, res[0].transcript);
    };
    rec.onend = () => browserSession === r.current && stateRef.current !== "idle" && rec.start();
    rec.onerror = (e) => e.error !== "no-speech" && setError(`Web Speech API : ${e.error}`);
    rec.start();
    r.current.sr = rec;
  };

  const start = async () => {
    const ticket = ++epoch.current;
    setS("processing");
    setError(null);
    try {
      if (serverVoice) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
        if (ticket !== epoch.current) { stream.getTracks().forEach((t) => t.stop()); return; }
        r.current.stream = stream;
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        ctx.createMediaStreamSource(stream).connect(analyser);
        r.current = { stream, ctx, analyser, buf: new Float32Array(analyser.fftSize) };
        setS("processing");
        loop();
        await finish(null, null, true);
      } else {
        setS("processing");
        await finish(null, null, true);
        if (ticket === epoch.current && stateRef.current !== "idle") startBrowser();
      }
    } catch (e) {
      if (ticket !== epoch.current) return;
      setError(e.message || String(e));
      stop();
    }
  };

  const stop = useCallback(() => {
    epoch.current += 1;
    setS("idle");
    const c = r.current;
    c.cancelWaiting?.();
    cancelAnimationFrame(c.raf);
    try { c.recorder?.stop(); } catch {}
    c.stream?.getTracks().forEach((t) => t.stop());
    c.ctx?.close();
    c.resolvePlayback?.();
    c.audio?.pause();
    if (c.audioUrl) URL.revokeObjectURL(c.audioUrl);
    try { c.sr?.stop(); } catch {}
    window.speechSynthesis?.cancel();
    r.current = {};
    setLevel(0);
  }, []);

  useEffect(() => stop, [stop]);
  return { state, level, error, start, stop, interrupt };
}
